/* ============================================================
   ct-intake.js — shared behaviour for the store's intake forms
   (ct-contact-form, ct-support-form, ct-consultation-form,
   ct-tax-exempt-upload).

   Data-attribute driven, so one implementation serves every intake
   and a fix lands everywhere at once. Self-initialising over every
   [data-ct-intake] root on the page, and idempotent, so it survives
   a section re-render in the theme customizer.

   TRANSPORT (2026-09-29, v2): the receiving endpoint is Formspree.
   The form posts by fetch() as multipart/form-data with
   Accept: application/json, so file uploads ride along and the
   customer never leaves the page — the receipt panel replaces the
   form in place. If JavaScript is unavailable the form still posts
   natively; Formspree honours the `_next` hidden field and sends the
   customer back here with ?submitted=1, which shows the same receipt.

   It does five things:
     1. Shows the receipt panel on ?submitted=1 (the no-JS fallback).
     2. Hard-blocks submission while the form is dormant (no action).
     3. Validates the two fields the reply depends on — a reachable
        email, and a phone number with enough digits if one was given.
        A form can mark phone required with data-ct-intake-phone-required.
     4. Checks an optional file against data-ct-intake-max-bytes.
     5. Submits by fetch(), shows the receipt on success, and on
        failure shows the endpoint's message and re-enables the button
        so a slow or failed post never reads as a dead form.

   It does NOT check the honeypot (`_gotcha`). That is for the
   endpoint to reject on; a client-side check would only teach a bot
   to leave it empty.

   KLAVIYO CONFIRMATION EVENT (2026-10-23, v3): a form can opt in to
   firing a client-side Klaviyo event on successful submit by setting
   data-ct-intake-klaviyo-key (account public key) and
   data-ct-intake-klaviyo-metric (event name) — the tax-exempt form
   uses this so the branded "we received it" flow sends. Fire-and-
   forget: it never blocks or delays the receipt panel, and a Klaviyo
   outage cannot break the form. Mirrors Clear Digital's
   cd-tax-exempt-upload implementation. Profile email comes from the
   validated email field; the certificate expiry date (if the form has
   [data-ct-intake-expiry]) is written to the profile as
   tax_exempt_expiry, which the expiry-reminder flow keys off.
   ============================================================ */
(function () {
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function setStatus(status, msg, isErr) {
    if (!status) return;
    var base = (status.getAttribute('data-ct-intake-status-class') || status.className.split(' ')[0] || 'ct-intake__status');
    status.className = base + (isErr ? ' ' + base + '--err' : '');
    status.textContent = msg || '';
  }

  function init(root) {
    if (!root || root.hasAttribute('data-ct-intake-ready')) return;
    var form = root.querySelector('[data-ct-intake-form]');
    if (!form) return;
    root.setAttribute('data-ct-intake-ready', '');

    var body = root.querySelector('[data-ct-intake-body]');
    var success = root.querySelector('[data-ct-intake-success]');
    var btn = form.querySelector('[data-ct-intake-submit]');
    var status = form.querySelector('[data-ct-intake-status]');
    var emailField = form.querySelector('[data-ct-intake-email]');
    var phoneField = form.querySelector('[data-ct-intake-phone]');
    var fileField = form.querySelector('[data-ct-intake-file]');
    var replyTo = form.querySelector('[data-ct-intake-replyto]');
    var maxBytes = parseInt(form.getAttribute('data-ct-intake-max-bytes') || '0', 10);
    var phoneRequired = form.hasAttribute('data-ct-intake-phone-required');
    var sendingText = form.getAttribute('data-ct-intake-sending') || 'Sending…';
    var klaviyoKey = form.getAttribute('data-ct-intake-klaviyo-key');
    var klaviyoMetric = form.getAttribute('data-ct-intake-klaviyo-metric');

    // Optional Klaviyo event on success (see header). Never throws, never blocks.
    function fireKlaviyo() {
      if (!klaviyoKey || !klaviyoMetric || !window.fetch) return;
      try {
        var email = emailField ? (emailField.value || '').trim() : '';
        if (!email) return;
        var expiryEl = form.querySelector('[data-ct-intake-expiry]');
        var companyEl = form.querySelector('[data-ct-intake-company]');
        var storeEl = form.querySelector('[name="source_store"]');
        var firstName = form.getAttribute('data-ct-intake-klaviyo-first-name');
        var profileAttrs = { email: email };
        if (firstName) profileAttrs.first_name = firstName;
        if (expiryEl && expiryEl.value) profileAttrs.properties = { tax_exempt_expiry: expiryEl.value };
        fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(klaviyoKey), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
          body: JSON.stringify({ data: { type: 'event', attributes: {
            properties: {
              business_name: companyEl ? companyEl.value : '',
              source: 'tax-exempt-cert-form',
              source_store: storeEl ? storeEl.value : ''
            },
            metric: { data: { type: 'metric', attributes: { name: klaviyoMetric } } },
            profile: { data: { type: 'profile', attributes: profileAttrs } }
          } } })
        }).catch(function () {});
      } catch (e) {}
    }

    function showReceipt() {
      if (body) body.hidden = true;
      if (success) {
        success.hidden = false;
        try { success.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
      }
    }

    // Landed back here from the endpoint's return URL (no-JS path) —
    // the receipt replaces the form rather than sitting above it.
    try {
      if (window.location.search.indexOf('submitted=1') > -1 && success && body) {
        showReceipt();
        return;
      }
    } catch (e) {}

    function fail(msg, field) {
      setStatus(status, msg, true);
      if (btn) btn.disabled = false;
      if (field && field.focus) field.focus();
    }

    form.addEventListener('submit', function (e) {
      // Dormant build: nowhere to post to, so never let a submit through.
      if (form.hasAttribute('data-ct-intake-disabled') || !form.getAttribute('action')) {
        e.preventDefault();
        return;
      }

      // The email is how the reply gets back.
      if (emailField && !EMAIL.test((emailField.value || '').trim())) {
        e.preventDefault();
        fail('Please enter a valid email address so we can reply.', emailField);
        return;
      }

      if (phoneField) {
        var digits = (phoneField.value || '').replace(/\D/g, '');
        if ((digits.length && digits.length < 10) || (phoneRequired && digits.length < 10)) {
          e.preventDefault();
          fail(phoneRequired
            ? 'Please enter a phone number with at least 10 digits so we can reach you.'
            : 'That phone number looks short — please include the area code, or clear the field.', phoneField);
          return;
        }
      }

      if (fileField && maxBytes && fileField.files && fileField.files.length && fileField.files[0].size > maxBytes) {
        e.preventDefault();
        fail('That file is larger than ' + Math.round(maxBytes / 1048576) + ' MB. Try a smaller scan or a photo instead.', fileField);
        return;
      }

      // Reply-to for the notification email, mirrored from the email field.
      if (replyTo && emailField) replyTo.value = (emailField.value || '').trim();

      // No fetch (very old browser): let the native post run; `_next` brings them back.
      if (!window.fetch || !window.FormData) {
        if (btn) btn.disabled = true;
        setStatus(status, sendingText, false);
        return;
      }

      e.preventDefault();
      if (btn) btn.disabled = true;
      setStatus(status, sendingText, false);

      var data = new FormData(form);
      fetch(form.getAttribute('action'), {
        method: 'POST',
        body: data,
        headers: { 'Accept': 'application/json' }
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (json) {
          if (res.ok) {
            setStatus(status, '', false);
            fireKlaviyo();
            showReceipt();
            return;
          }
          var msg = 'Something went wrong sending that. Please try again, or call 864-973-7973.';
          if (json && json.errors && json.errors.length) {
            msg = json.errors.map(function (er) { return er.message || ''; }).filter(Boolean).join(' ') || msg;
          } else if (json && json.error) {
            msg = json.error;
          }
          fail(msg);
        });
      }).catch(function () {
        fail('We couldn’t reach the server. Check your connection and try again, or call 864-973-7973.');
      });
    });
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-ct-intake]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme customizer re-renders a section's markup in place.
  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });
})();
